import React, { useState, useEffect } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform, TouchableWithoutFeedback, Keyboard, Alert, Image, ScrollView, Modal, FlatList, TouchableOpacity } from 'react-native';
import { TextInput, Button, Text, Divider } from 'react-native-paper';
import * as FileSystem from 'expo-file-system/legacy';
import { useAuth } from '../../contexts/AuthContext';

const LoginScreen = ({ navigation }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [secureTextEntry, setSecureTextEntry] = useState(true);
  const [customLogo, setCustomLogo] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const { login, error, loading, tenantSelectionRequired, availableTenants } = useAuth();

  useEffect(() => {
    loadCustomLogo();
  }, []);

  const loadCustomLogo = async () => {
    try {
      const logoPath = FileSystem.documentDirectory + 'logo.png';
      const logoExists = await FileSystem.getInfoAsync(logoPath);
      if (logoExists.exists) {
        setCustomLogo(logoPath);
      }
    } catch (error) {
      console.log('No se pudo cargar el logo personalizado:', error);
    }
  };

  const handleLogin = async () => {
    if (!username || !password) {
      Alert.alert('Error', 'Por favor ingresa tu usuario y contrasena');
      return;
    }

    const result = await login(username, password, tenantSlug || null);
    if (!result) {
      // Check if tenant selection is needed
    }
  };

  // Manejar seleccion de tenant requerida
  useEffect(() => {
    if (tenantSelectionRequired && availableTenants.length > 0) {
      setShowTenantModal(true);
    }
  }, [tenantSelectionRequired, availableTenants]);

  const handleSelectTenant = async (slug) => {
    setShowTenantModal(false);
    setTenantSlug(slug);
    await login(username, password, slug);
  };

  useEffect(() => {
    if (error) {
      Alert.alert('Error de inicio de sesion', error);
    }
  }, [error]);

  const content = (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <ScrollView contentContainerStyle={styles.innerContainer} keyboardShouldPersistTaps="handled">
          <View style={styles.logoContainer}>
            {customLogo ? (
              <Image source={{ uri: customLogo }} style={styles.customLogo} resizeMode="contain" />
            ) : (
              <>
                <Text style={styles.logoText}>VA</Text>
                <Text style={styles.appName}>Ventas App</Text>
              </>
            )}
          </View>

          <View style={styles.formContainer}>
            <TextInput
              label="Codigo de empresa (opcional)"
              value={tenantSlug}
              onChangeText={setTenantSlug}
              style={styles.input}
              autoCapitalize="none"
              left={<TextInput.Icon icon="domain" />}
              placeholder="ej: mi-empresa"
            />

            <TextInput
              label="Usuario"
              value={username}
              onChangeText={setUsername}
              style={styles.input}
              autoCapitalize="none"
              left={<TextInput.Icon icon="account" />}
            />

            <TextInput
              label="Contrasena"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={secureTextEntry}
              style={styles.input}
              right={
                <TextInput.Icon
                  icon={secureTextEntry ? "eye" : "eye-off"}
                  onPress={() => setSecureTextEntry(!secureTextEntry)}
                />
              }
              left={<TextInput.Icon icon="lock" />}
            />

            <Button
              mode="contained"
              onPress={handleLogin}
              style={styles.button}
              loading={loading}
              disabled={loading}
            >
              Iniciar Sesion
            </Button>

            <Button
              mode="text"
              onPress={() => navigation?.navigate?.('RegistroEmpresa')}
              style={styles.registerButton}
              textColor="#007bff"
            >
              Registrar mi empresa
            </Button>
          </View>

          <View style={styles.footer}>
            <Text style={styles.footerText}>Ventas App - Sistema ERP Multi-Empresa</Text>
          </View>
        </ScrollView>

        {/* Modal de seleccion de tenant */}
        <Modal visible={showTenantModal} transparent animationType="slide">
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Seleccionar Empresa</Text>
              <Text style={styles.modalSubtitle}>Tu cuenta existe en varias empresas. Selecciona una:</Text>
              <Divider style={{ marginVertical: 10 }} />
              <FlatList
                data={availableTenants}
                keyExtractor={(item) => item.slug}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.tenantItem}
                    onPress={() => handleSelectTenant(item.slug)}
                  >
                    <Text style={styles.tenantName}>{item.nombre}</Text>
                    <Text style={styles.tenantSlugText}>{item.slug}</Text>
                  </TouchableOpacity>
                )}
              />
              <Button mode="text" onPress={() => setShowTenantModal(false)} style={{ marginTop: 10 }}>
                Cancelar
              </Button>
            </View>
          </View>
        </Modal>
      </KeyboardAvoidingView>
  );

  return Platform.OS === 'web' ? content : (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      {content}
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  innerContainer: { flexGrow: 1, justifyContent: 'center', padding: 20 },
  logoContainer: { alignItems: 'center', marginBottom: 40 },
  logoText: { fontSize: 50, fontWeight: 'bold', color: '#2196F3', marginBottom: 10 },
  appName: { fontSize: 24, fontWeight: 'bold', color: '#007bff' },
  customLogo: { width: 200, height: 120, marginBottom: 20 },
  formContainer: { marginBottom: 30 },
  input: { marginBottom: 16, backgroundColor: 'white' },
  button: { marginTop: 10, paddingVertical: 8, backgroundColor: '#007bff' },
  registerButton: { marginTop: 8 },
  footer: { alignItems: 'center' },
  footerText: { color: '#888', fontSize: 12 },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: 20 },
  modalContent: { backgroundColor: '#fff', borderRadius: 12, padding: 20, maxHeight: '60%' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  modalSubtitle: { fontSize: 14, color: '#666', marginTop: 4 },
  tenantItem: { paddingVertical: 14, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: '#eee' },
  tenantName: { fontSize: 16, fontWeight: '600', color: '#333' },
  tenantSlugText: { fontSize: 12, color: '#888', marginTop: 2 },
});

export default LoginScreen;
